import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { Focus, Minimize2 } from 'lucide-react';
import { Button } from '../ui/button';

/**
 * A4. Focus Mode — a single toggle that:
 *   - hides the app chrome (header + tabs + sidebars via `.lf-chrome` / [data-focus-hide])
 *   - dims non-selected categories on the current Stack Map layout
 *   - keeps only the visualization + minimal exit affordance
 *
 * Activated by pressing `Shift+F` (unless typing) or clicking the button.
 * Focus state is tracked via a body data-attr so any component can style
 * itself against it via CSS: `body[data-focus-mode="on"] .lf-chrome { ... }`.
 *
 * IMPLEMENTATION NOTE:
 * We use a React Context so `<FocusModeToggle />` and `<FocusModeExitOverlay />`
 * share the same on/off state. Mount `<FocusModeProvider>` high in the tree.
 */

const BODY_ATTR = 'data-focus-mode';
const STORAGE_KEY = 'lodgiflow_focus_mode';

const FocusModeContext = createContext(null);

export function FocusModeProvider({ children }) {
  const [on, setOn] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) === '1'; } catch { return false; }
  });

  // Body attr + persistence
  useEffect(() => {
    if (on) {
      document.body.setAttribute(BODY_ATTR, 'on');
      try { localStorage.setItem(STORAGE_KEY, '1'); } catch {}
    } else {
      document.body.removeAttribute(BODY_ATTR);
      try { localStorage.removeItem(STORAGE_KEY); } catch {}
    }
  }, [on]);

  // Shift+F to toggle (unless typing / modifier combos)
  useEffect(() => {
    const handler = (e) => {
      if (!e.shiftKey) return;
      if (e.key !== 'F' && e.key !== 'f') return;
      const t = e.target;
      const tag = (t?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select' || t?.isContentEditable) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      e.preventDefault();
      setOn(v => !v);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  // Esc exits focus mode
  useEffect(() => {
    if (!on) return;
    const handler = (e) => { if (e.key === 'Escape') setOn(false); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [on]);

  const toggle = useCallback(() => setOn(v => !v), []);
  const value = { on, setOn, toggle };
  return <FocusModeContext.Provider value={value}>{children}</FocusModeContext.Provider>;
}

export function useFocusMode() {
  const ctx = useContext(FocusModeContext);
  if (!ctx) {
    // Safe fallback when a consumer renders outside the provider —
    // still returns a valid shape so we never throw at runtime.
    return { on: false, setOn: () => {}, toggle: () => {} };
  }
  return ctx;
}

export default function FocusModeToggle({ className = '' }) {
  const { on, toggle } = useFocusMode();
  return (
    <Button
      type="button"
      variant={on ? 'default' : 'outline'}
      size="sm"
      onClick={toggle}
      className={`h-8 gap-1.5 ${on ? 'bg-violet-600 hover:bg-violet-700 text-white' : ''} ${className}`}
      title={on ? 'Exit focus mode (Esc)' : 'Enter focus mode (Shift+F)'}
      data-testid="focus-mode-toggle"
    >
      {on ? <Minimize2 className="h-3.5 w-3.5" /> : <Focus className="h-3.5 w-3.5" />}
      <span className="text-xs">{on ? 'Exit focus' : 'Focus'}</span>
    </Button>
  );
}

/**
 * Floating exit affordance rendered when focus mode is on. Sits in the
 * top-right corner above the visualization.
 */
export function FocusModeExitOverlay() {
  const { on, setOn } = useFocusMode();
  if (!on) return null;
  return (
    <div className="fixed top-3 right-3 z-[9999]" data-testid="focus-mode-exit-overlay">
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={() => setOn(false)}
        className="h-8 gap-1.5 shadow-lg bg-card/95 backdrop-blur"
      >
        <Minimize2 className="h-3.5 w-3.5" />
        <span className="text-xs">Exit focus</span>
        <kbd className="ml-1 text-[9px] px-1 py-0.5 rounded bg-muted text-muted-foreground">Esc</kbd>
      </Button>
    </div>
  );
}
